import { createElement } from "react";
import type { RowData } from "@tanstack/react-table";
import { DragHandleCell } from "../cells/DragHandleCell";
import { ExpandAllHeader, ExpandCell } from "../cells/ExpandCell";
import { RowNumberCell } from "../cells/RowNumberCell";
import { SelectAllHeader, SelectCell } from "../cells/SelectCell";
import { cellOf, displayColumn } from "./columnFactories";
import type {
    DataGridActionsColumnOptions,
    DataGridColumn,
    DataGridDisplayColumnOptions,
    DataGridExpandColumnOptions,
    DataGridSelectColumnOptions,
} from "./types";

/** The row checkbox (`NativeCheckbox`) is 16px square. */
const SELECT_CONTENT_WIDTH = 16;
/** The chevron and grip buttons are 20px square (`cells.module.scss`). */
const ROW_BUTTON_CONTENT_WIDTH = 20;

/**
 * Display columns that hold no value: selection, expansion, the drag
 * handle, row actions, row numbers, and header groups. Spread into
 * `createColumns`.
 */
export const createDisplayColumns = <TData extends RowData>() => ({
    select: ({
        visibility = "engaged",
        ...options
    }: DataGridSelectColumnOptions<TData> = {}) =>
        displayColumn<TData>({
            kind: "select",
            id: "select",
            options: { header: "Select rows", ...options },
            cell: (context, meta) =>
                createElement(SelectCell<TData>, { context, meta, visibility }),
            header: (context) =>
                createElement(SelectAllHeader<TData>, { context }),
            contentWidth: SELECT_CONTENT_WIDTH,
            skeleton: visibility === "always" ? "checkbox" : "none",
        }),
    expand: ({
        expandAll = false,
        ...options
    }: DataGridExpandColumnOptions<TData> = {}) =>
        displayColumn<TData>({
            kind: "expand",
            id: "expand",
            options: { header: "Expand rows", ...options },
            cell: cellOf<TData>(ExpandCell),
            header: expandAll
                ? (context) =>
                      createElement(ExpandAllHeader<TData>, { context })
                : undefined,
            contentWidth: ROW_BUTTON_CONTENT_WIDTH,
        }),
    /** Grip for `rowReorder`; pin it to the start with `select`. */
    dragHandle: (options: DataGridDisplayColumnOptions<TData> = {}) =>
        displayColumn<TData>({
            kind: "dragHandle",
            id: "dragHandle",
            options: { header: "Reorder rows", ...options },
            cell: cellOf<TData>(DragHandleCell),
            contentWidth: ROW_BUTTON_CONTENT_WIDTH,
        }),
    /** `render` returns an `ActionsCell` with the row's buttons and menu. */
    actions: ({ width, ...options }: DataGridActionsColumnOptions<TData>) =>
        displayColumn<TData>({
            kind: "actions",
            id: "actions",
            options: { header: "Row actions", ...options, size: width },
            cell: cellOf<TData>(options.render),
        }),
    /** The row's visible 1-based position, not its index in the data. */
    rowNumber: (options: DataGridDisplayColumnOptions<TData> = {}) =>
        displayColumn<TData>({
            kind: "rowNumber",
            id: "rowNumber",
            options: { header: "Row number", ...options },
            cell: cellOf<TData>(RowNumberCell),
        }),
    /** `id` is required: headers repeat across groups and change with copy edits. */
    group: (options: {
        id: string;
        header: string;
        columns: ReadonlyArray<DataGridColumn<TData>>;
    }): DataGridColumn<TData> => ({
        id: options.id,
        header: options.header,
        meta: { headerLabel: options.header },
        columns: [...options.columns],
    }),
});
